/** One app, an interface per profile (M19, ADR-110). The profile only chooses what the screens OFFER (the bottom tabs, the cards of Today): it is
 *  cosmetic like every UI check; the server decides from `core.role_permission`, always. The direction (the superintendent, the club's director and
 *  its owner) has every function; a jardinier sees the whole team's planning, the scanner, his day and the map. */
export type Profile = 'direction' | 'mechanic' | 'field' | 'store' | 'kiosk' | 'viewer'

/** The three roles that hold every permission of the club (the head greenkeeper is the club's superintendent). */
export const DIRECTION_ROLES: readonly string[] = ['head_greenkeeper', 'manager', 'admin']

/** The profile of a person from their roles at the site; a shared workstation is always `kiosk`, whoever is unlocked on it. A person with several
 *  roles gets the widest interface: direction, then the workshop, then the course, then the store. */
export function profileOf(siteRoles: readonly string[], kiosk: boolean): Profile {
  if (kiosk) return 'kiosk'
  const has = (r: string) => siteRoles.includes(r)
  if (DIRECTION_ROLES.some(has)) return 'direction'
  if (has('mechanic')) return 'mechanic'
  if (has('crew')) return 'field'
  if (has('storekeeper')) return 'store'
  return 'viewer'
}

export type NavPath = '/' | '/plan' | '/map' | '/assets' | '/scan' | '/settings' | '/workshop' | '/catalogue'
/** `short` is what fits under an icon at phone width; the full name (`key`) stays the accessible name, which the end-to-end tests use. */
export interface NavItem { to: NavPath; key: string; short?: string; icon: string }

/** M23 item 4: on a phone the label under the icon is the short word the owner chose ("Jour", "Engins"); from `sm:` the full name is shown. */
const TAB: Record<'today' | 'plan' | 'map' | 'assets' | 'scan' | 'settings' | 'workshop' | 'catalogue', NavItem> = {
  today: { to: '/', key: 'nav.today', short: 'nav.todayShort', icon: 'today' },
  plan: { to: '/plan', key: 'nav.plan', icon: 'calendar' },
  map: { to: '/map', key: 'nav.map', icon: 'map' },
  assets: { to: '/assets', key: 'nav.assets', short: 'nav.assetsShort', icon: 'mower' },
  scan: { to: '/scan', key: 'nav.scan', icon: 'scan' },
  settings: { to: '/settings', key: 'nav.settings', icon: 'settings' },
  workshop: { to: '/workshop', key: 'nav.workshop', icon: 'wrench' },
  catalogue: { to: '/catalogue', key: 'nav.catalogue', icon: 'box' },
}

/** The roots of the bottom tabs, whoever's tabs they are: a screen at one of these paths has no way back in the header (M23 item 1); every
 *  other screen gets a 56-px back control there, to the parent its route declares (`staticData.parent` in router.tsx). */
export const TAB_ROOTS: readonly NavPath[] = Object.values(TAB).map((n) => n.to)
export const isTabRoot = (path: string): boolean => (TAB_ROOTS as readonly string[]).includes(path.replace(/\/+$/, '') || '/')

/** The bottom tabs of a profile, in their order (at most six: a phone has room for no more). Synchronisation is not a tab: the header's badge opens it,
 *  for everybody. A workstation keeps what it had (no settings, no sync page). */
export function navFor(profile: Profile): NavItem[] {
  const { today, plan, map, assets, scan, settings, workshop, catalogue } = TAB
  switch (profile) {
    case 'direction': return [today, plan, map, assets, scan, settings]
    case 'mechanic': return [today, workshop, assets, plan, scan, settings]
    case 'field': return [today, plan, map, scan, settings]
    case 'store': return [today, catalogue, scan, settings]
    case 'kiosk': return [today, plan, assets, scan]
    case 'viewer': return [today, map, settings]
  }
}
